import { jwtDecode } from 'jwt-decode'
import { v4 as uuid } from 'uuid'

import { Maybe, MFAStatus, User } from '../common/types'
import { Item, ItemGroup, Preferences } from '../containers/Parameters/types'

import { BackendNotification } from '../util/notifications/types'
import { ArcCodeLists } from '../components/ALSubForm/types'
import { RepairShop } from '../containers/TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { TOWING_JOB_FILTERS_KEY } from '../components/TowingJobFilter/useTowingJobFilters'
import { canSendMessageToWebView, sendMessageToWebView } from '../util/react-native'
import { WebMessageType } from '../util/react-native/types'
import { datadogRum } from '@datadog/browser-rum'
import { DataDogContextKeys } from '../datadog'

const STORED_REFRESH_TOKEN = 'refresh_token'
const STORED_AUTHTOKEN_KEY = 'authorization'
const STORED_AUTHTOKEN_EXP = 'expiration'
const STORED_ITEMGROUPS_KEY = 'item_groups'
const STORED_ITEMS_KEY = 'items'
const STORED_CUSTOMER_ID_KEY = 'user_id'
const STORED_COMPANY_ID_KEY = 'company_id'
const STORED_SERVICEWORKER_ENDPOINT = 'serviceworker_endpoint'
const STORED_AL_ARC_CODES_DATA = 'al_arc_codes'
const STORED_AL_REPAIR_SHOPS_DATA = 'al_repair_shops'
const STORED_USER_PREFERENCES = 'user_preferences'
// const STORED_FILTERS = 'filters'
// const STORED_ADD_TOWING_ORDER = 'addTowingOrder'
// const STORED_EDIT_TOWING_ORDER = 'editTowingOrder'
// const STORED_TOWING_RECORD = 'towingRecord'
const STORED_TRACKED_RECORDS = 'tracked_records'
const STORED_IS_WEBVIEW = 'is_webview'
const STORED_APP_INFO = 'app_info'
const STORED_BACKEND_NOTIFICATIONS = 'backend_notifications'
const STORED_BACKEND_NOTIFICATIONS_FETCHED = 'backend_notifications_fetched'
const STORED_MFA_STATUS = 'mfa_status'
const STORED_APPLICATION_ID = 'application_id'
const STORED_DEVICE_ID = 'device_id'
const STORED_LOCATION_TOKEN = 'location_token'

export interface DecodedRefreshToken {
  type: 'refresh_token'
  user_id: number
  username: string
  display_name: string
  remember_me_for_days: string
  application_id: 'ih_app'
  device_id: string | null
  exp: number
}

export interface DecodedAccessToken {
  type: 'access_token'
  user_id: number
  username: string
  display_name: string
  remember_me_for_days: string
  user_role: string
  access_level: number
  company_id: number
  mfa_status: MFAStatus | string
  application_id: string
  device_id: string | null
  exp: number
}

export function hasStoredLogin(): boolean {
  return !!window.localStorage.getItem(STORED_AUTHTOKEN_KEY)
}
export function getStoredLogin(): string | null {
  return window.localStorage.getItem(STORED_AUTHTOKEN_KEY)
}
export function getStoredRefreshToken(): string | null {
  return window.localStorage.getItem(STORED_REFRESH_TOKEN)
}
export function getStoredLocationToken(): string | null {
  return window.localStorage.getItem(STORED_LOCATION_TOKEN)
}
export function setStoredRefreshToken(token: string): void {
  window.localStorage.setItem(STORED_REFRESH_TOKEN, token)
}

export function removeStoredRefreshToken(): void {
  window.localStorage.removeItem(STORED_REFRESH_TOKEN)
}

export function getStoredLoginExpiration(): number | null {
  const exp = window.localStorage.getItem(STORED_AUTHTOKEN_EXP)
  if (exp) {
    return (exp as unknown) as number
  }
  return null
}

export function hasStoredCompanyId(): boolean {
  return !!window.localStorage.getItem(STORED_COMPANY_ID_KEY)
}

export function getStoredCompanyId(): string | null {
  return window.localStorage.getItem(STORED_COMPANY_ID_KEY)
}

export function getStoredUserId(): number | null {
  const stored = window.localStorage.getItem(STORED_CUSTOMER_ID_KEY)

  if (!stored) {
    return null
  }

  const result = parseInt(stored, 10)

  if (isNaN(result)) {
    return null
  }

  return result
}

export function storeLogin({ accessToken, refreshToken, locationToken }: {accessToken: string, refreshToken: string, locationToken: string}) {
  const tokenData = jwtDecode<DecodedAccessToken>(accessToken)

  window.localStorage.setItem(STORED_AUTHTOKEN_KEY, accessToken)
  window.localStorage.setItem(STORED_REFRESH_TOKEN, refreshToken)
  window.localStorage.setItem(STORED_LOCATION_TOKEN, locationToken)
  window.localStorage.setItem(STORED_AUTHTOKEN_EXP, (tokenData.exp as unknown) as string)
  window.localStorage.setItem(STORED_CUSTOMER_ID_KEY, tokenData.user_id.toString())
  window.localStorage.setItem(STORED_COMPANY_ID_KEY, tokenData.company_id.toString())
  window.localStorage.setItem(STORED_MFA_STATUS, tokenData.mfa_status)

  const deviceId = getOrGenerateDeviceID()
  const appID = isRunningInWebview() ? 'innomobiili' : 'innohinaus-app';
  setApplicationID(appID)

  if (canSendMessageToWebView()) {
    sendMessageToWebView({ type: WebMessageType.LOGIN, data: JSON.stringify({ "locationToken": getStoredLocationToken()}) })
  }

  const appInfo = getStoredAppInfo()

  datadogRum.setUser({
    id: tokenData.user_id.toString(), 
    companyId: tokenData.company_id.toString(),
    name: tokenData.username,
    role: tokenData.user_role
  });

 
  datadogRum.setGlobalContextProperty(DataDogContextKeys.ApplicationId, appID);
  datadogRum.setGlobalContextProperty(DataDogContextKeys.DeviceId, deviceId);

  if (appInfo) {
    datadogRum.setGlobalContextProperty(DataDogContextKeys.OS, appInfo.platform.OS);
    datadogRum.setGlobalContextProperty(DataDogContextKeys.PlatformVersion, appInfo.platform.version);
    datadogRum.setGlobalContextProperty(DataDogContextKeys.BuildNumber, appInfo.buildNumber);
    datadogRum.setGlobalContextProperty(DataDogContextKeys.AppVersion, appInfo.version);
  }

}

export function storeMfaStatus(status: MFAStatus) {
  window.localStorage.setItem(STORED_MFA_STATUS, status)
}

export function getMfaStatus(): MFAStatus | null {
  return window.localStorage.getItem(STORED_MFA_STATUS) as MFAStatus | null
}

export function storeLogout() {
  const socketMessages = window.localStorage.getItem('socketMessages')
  const theme = window.localStorage.getItem('theme')
  const persistentItems = { socketMessages, theme }

  const deviceId = getDeviceID()
  const applicationID = getApplicationID()
  const isWebview = isRunningInWebview()

  window.localStorage.clear()

  if (persistentItems) {
    persistentItems.socketMessages && window.localStorage.setItem('socketMessages', persistentItems.socketMessages)
    persistentItems.theme && window.localStorage.setItem('theme', persistentItems.theme)
  }

  if (deviceId) {
    setDeviceID(deviceId)
  }

  if (applicationID) {
    setApplicationID(applicationID)
  }

  if(isWebview || canSendMessageToWebView()){
    window.localStorage.setItem(STORED_IS_WEBVIEW, 'true')
  }
  datadogRum.clearUser()
  datadogRum.clearGlobalContext();

  window.location.reload()
}

export const storeServiceWorkerEndpoint = (endpoint: string) => {
  window.localStorage.setItem(STORED_SERVICEWORKER_ENDPOINT, endpoint)
}

export const removeServiceWorkerEndpoint = () => {
  window.localStorage.removeItem(STORED_SERVICEWORKER_ENDPOINT)
}

export const getServiceWorkerEndpoint = () => window.localStorage.getItem(STORED_SERVICEWORKER_ENDPOINT)

export function hasStoredItemGroups(): boolean {
  return !!window.localStorage.getItem(STORED_ITEMGROUPS_KEY)
}

export function getStoredItemGroups(): Maybe<ItemGroup[]> {
  const itemGroups = window.localStorage.getItem(STORED_ITEMGROUPS_KEY)
  return itemGroups ? JSON.parse(itemGroups) : null
}

export function storeItemGroups(itemGroups: ItemGroup[]) {
  window.localStorage.setItem(STORED_ITEMGROUPS_KEY, JSON.stringify(itemGroups))
}

export function hasStoredItems(): boolean {
  return !!window.localStorage.getItem(STORED_ITEMS_KEY)
}

export function getStoredItems(): Maybe<Item[]> {
  const items = window.localStorage.getItem(STORED_ITEMS_KEY)
  return items ? JSON.parse(items) : null
}

export function storeItems(items: Item[]) {
  window.localStorage.setItem(STORED_ITEMS_KEY, JSON.stringify(items))
}


export function storeUserPreferences(preferences: Preferences) {
  window.localStorage.setItem(STORED_USER_PREFERENCES, JSON.stringify(preferences))
}

export function hasStoredStaticData(): boolean {
  return !!window.localStorage.getItem(STORED_AL_ARC_CODES_DATA)
}

export function getStoredStaticData(): Maybe<ArcCodeLists> {
  const staticData = window.localStorage.getItem(STORED_AL_ARC_CODES_DATA)
  return staticData ? JSON.parse(staticData) : null
}

export function storeStaticData(data: ArcCodeLists) {
  window.localStorage.setItem(STORED_AL_ARC_CODES_DATA, JSON.stringify(data))
}

export function hasStoredAlRepairShopsData(): boolean {
  return !!window.localStorage.getItem(STORED_AL_REPAIR_SHOPS_DATA)
}

export function getStoredAlRepairShopData(): Maybe<RepairShop[]> {
  const staticData = window.localStorage.getItem(STORED_AL_REPAIR_SHOPS_DATA)
  return staticData ? JSON.parse(staticData) : null
}

export function storeAlRepairShopData(data: RepairShop) {
  window.localStorage.setItem(STORED_AL_REPAIR_SHOPS_DATA, JSON.stringify(data))
}

// export function getStoredStaticData(): Maybe<SosArcCodesFilters> {
//   const staticData = window.localStorage.getItem(STORED_STATIC_DATA)
//   return staticData ? JSON.parse(staticData) : null
// }

// export function storeStaticData(data: SosArcCodesFilters) {
//   window.localStorage.setItem(STORED_STATIC_DATA, JSON.stringify(data))
// }

export function storeTrackedRecords(records: Maybe<number[]>) {
  window.localStorage.setItem(STORED_TRACKED_RECORDS, JSON.stringify(records))
}

export function hasStoredTrackedRecords(): boolean {
  const records = getStoredTrackedRecords()
  return Array.isArray(records) && records.length > 0
}

export function getStoredTrackedRecords(): Maybe<number[]> {
  const trackedRecords = window.localStorage.getItem(STORED_TRACKED_RECORDS)
  return trackedRecords ? JSON.parse(trackedRecords) : null
}

export function isRunningInWebview(): boolean {
  const value = window.localStorage.getItem(STORED_IS_WEBVIEW)
  return value === null || value === '0' ? false : true
}


export interface AppInfo {
  platform: {
    OS: "ios" | "android" | "web" | "windows" | "macos",
    version: string | number
  }
  buildNumber: number
  version: string
}

export function hasRequiredVersionForFeature({ iOSVersion, AndroidVersion }: {iOSVersion: string, AndroidVersion: string}): boolean {
  const value = window.localStorage.getItem(STORED_APP_INFO)

  const appInfo: AppInfo = value ? JSON.parse(value) :null
  if(!appInfo) {
    return false
  }

  if(appInfo.platform.OS === 'ios') {
    return appInfo.version >= iOSVersion
  } else if(appInfo.platform.OS === 'android') {
    return appInfo.version >= AndroidVersion
  }

  return false
}

export function getStoredAppInfo(): Maybe<AppInfo> {
  const value = window.localStorage.getItem(STORED_APP_INFO)
  return value ? JSON.parse(value) : null
}

export function storeBackendNotifications(notifications: BackendNotification[]) {
  window.localStorage.setItem(STORED_BACKEND_NOTIFICATIONS, JSON.stringify(notifications))
}

export function getStoredBackendNotifications(): Maybe<BackendNotification[]> {
  const notifications = window.localStorage.getItem(STORED_BACKEND_NOTIFICATIONS)
  return notifications ? JSON.parse(notifications) : []
}

export function hasStoredBackendNotifications(): boolean {
  return !!window.localStorage.getItem(STORED_BACKEND_NOTIFICATIONS)
}

export function backendNotificationsFetched(): boolean {
  return !!window.localStorage.getItem(STORED_BACKEND_NOTIFICATIONS_FETCHED)
}

export function setBackendNotificationsFetched() {
  return window.localStorage.setItem(STORED_BACKEND_NOTIFICATIONS_FETCHED, '1')
}

export function removeStoredBackendNotifications() {
  window.localStorage.removeItem(STORED_BACKEND_NOTIFICATIONS)
  window.dispatchEvent(new Event('storage'))
}

export function getApplicationID(): string | null {
  return window.localStorage.getItem(STORED_APPLICATION_ID)
}

export function setApplicationID(id: string) {
  return window.localStorage.setItem(STORED_APPLICATION_ID, id)
}

/** Gets the device ID, generating a new one if it doesn't exist */
export function getOrGenerateDeviceID(): string {
  const deviceId = getDeviceID()
  
  if (!deviceId) {
    const newDeviceId = uuid()
    setDeviceID(newDeviceId)
    return newDeviceId
  }

  return deviceId
}

export function getDeviceID(): string | null {
  return window.localStorage.getItem(STORED_DEVICE_ID)
}

export function setDeviceID(id: string) {
  return window.localStorage.setItem(STORED_DEVICE_ID, id)
}

export const setLocalStorageTowingJobFilters = (value: any) => {
  localStorage.setItem(TOWING_JOB_FILTERS_KEY, JSON.stringify(value))
}

export const removeLocalStorageTowingJobFilters = () => {
  localStorage.removeItem(TOWING_JOB_FILTERS_KEY)
}

export function maybeRemoveFiltersInLocalStorage(user: User): void {
  if (!user || !user.user_id) return

  const storedValue = localStorage.getItem(TOWING_JOB_FILTERS_KEY)
  if (!storedValue) return

  try {
    const parsed = JSON.parse(storedValue) as { userId?: string }
    const shouldRemove = parsed.userId && Number(parsed.userId) !== user.user_id

    if (shouldRemove) {
      removeLocalStorageTowingJobFilters()
    }
  } catch (error) {
    removeLocalStorageTowingJobFilters()
  }
}
